import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';

const IdariIslerTaleplerimPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const { toasts, addToast, removeToast } = useToast();

  const fetchMyRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/administrative/requests');
      if (response.success) {
        setRequests(response.requests);
      }
    } catch (error) {
      console.error('Taleplerim yüklenirken hata:', error);
      addToast('Taleplerim yüklenirken bir hata oluştu', 'error', 5000);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchMyRequests();
  }, [fetchMyRequests]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'bg-warning', text: 'Beklemede', icon: 'bx-time' },
      approved: { class: 'bg-info', text: 'Onaylandı', icon: 'bx-check' },
      completed: { class: 'bg-success', text: 'Tamamlandı', icon: 'bx-check-circle' },
      rejected: { class: 'bg-danger', text: 'Reddedildi', icon: 'bx-x-circle' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`badge ${config.class} d-flex align-items-center gap-1`}>
        <i className={`bx ${config.icon}`}></i>
        {config.text}
      </span>
    );
  };

  const getCategoryText = (category) => {
    const categories = {
      stationery: 'Kırtasiye',
      phone: 'Telefon',
      computer: 'Bilgisayar Aksesuarları',
      other: 'Diğer'
    };
    return categories[category] || category;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { class: 'bg-secondary', text: 'Düşük' },
      normal: { class: 'bg-primary', text: 'Normal' },
      high: { class: 'bg-warning', text: 'Yüksek' },
      urgent: { class: 'bg-danger', text: 'Acil' }
    };
    
    const config = priorityConfig[priority] || priorityConfig.normal;
    return (
      <span className={`badge ${config.class}`}>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredRequests = requests.filter(request => {
    const statusMatch = !filterStatus || request.status === filterStatus;
    const categoryMatch = !filterCategory || request.category === filterCategory;
    return statusMatch && categoryMatch;
  });

  return (
    <div className="modern-hr-page">
      <div className="container-fluid">
        <div className="modern-hr-header">
          <div className="modern-hr-header-content">
            <div className="modern-hr-header-text">
              <h1>Taleplerim</h1>
              <p>Oluşturduğunuz idari işler taleplerini görüntüleyin ve takip edin</p>
            </div>
            <div className="modern-hr-header-actions">
              <div className="modern-hr-stats">
                <div className="modern-hr-stat-item">
                  <i className="bx bx-file-plus"></i>
                  <span>{requests.length} Talep</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtreler */}
        <div className="modern-hr-filters">
          <div className="row">
            <div className="col-md-3">
              <div className="modern-hr-form-group">
                <label className="modern-hr-form-label">Durum</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="modern-hr-form-select"
                >
                  <option value="">Tüm Durumlar</option>
                  <option value="pending">Beklemede</option>
                  <option value="approved">Onaylandı</option>
                  <option value="completed">Tamamlandı</option>
                  <option value="rejected">Reddedildi</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="modern-hr-form-group">
                <label className="modern-hr-form-label">Kategori</label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="modern-hr-form-select"
                >
                  <option value="">Tüm Kategoriler</option>
                  <option value="stationery">Kırtasiye</option>
                  <option value="phone">Telefon</option>
                  <option value="computer">Bilgisayar Aksesuarları</option>
                  <option value="other">Diğer</option>
                </select>
              </div>
            </div>
            <div className="col-md-6">
              <div className="modern-hr-form-group">
                <label className="modern-hr-form-label">&nbsp;</label>
                <div className="d-flex gap-2">
                  <button
                    className="modern-hr-btn modern-hr-btn-secondary"
                    onClick={() => {
                      setFilterStatus('');
                      setFilterCategory('');
                    }}
                  >
                    <i className="bx bx-refresh"></i>
                    Filtreleri Temizle
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Talep Listesi */}
        <div className="modern-hr-widget">
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bx-list-ul"></i>
              <h4>Talep Listesi</h4>
            </div>
            <div className="modern-hr-widget-actions">
              <span className="modern-hr-count">
                {filteredRequests.length} Talep
              </span>
            </div>
          </div>

          <div className="modern-hr-widget-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <p className="mt-3 text-muted">Talepler yükleniyor...</p>
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-5">
                <i className="bx bx-file-blank display-1 text-muted"></i>
                <h5 className="mt-3">Henüz talep bulunmuyor</h5>
                <p className="text-muted">İlk talebinizi oluşturmak için "Talep Oluştur" sayfasını ziyaret edin.</p>
              </div>
            ) : (
              <div className="row">
                {filteredRequests.map((request) => (
                  <div key={request._id} className="col-lg-6 col-xl-4 mb-4">
                    <div className="modern-request-card">
                      <div className="modern-request-card-header">
                        <div className="modern-request-card-title">
                          <h6>{request.item}</h6>
                          <p className="text-muted mb-0">{getCategoryText(request.category)}</p>
                        </div>
                        <div className="modern-request-card-badges">
                          {getStatusBadge(request.status)}
                          {getPriorityBadge(request.priority)}
                        </div>
                      </div>

                      <div className="modern-request-card-body">
                        <div className="modern-request-info">
                          <div className="modern-request-info-item">
                            <i className="bx bx-hash"></i>
                            <span>Miktar: {request.quantity}</span>
                          </div>
                          <div className="modern-request-info-item">
                            <i className="bx bx-calendar"></i>
                            <span>{formatDate(request.requestDate)}</span>
                          </div>
                          <div className="modern-request-info-item">
                            <i className="bx bx-building"></i>
                            <span>{request.department}</span>
                          </div>
                        </div>

                        {request.description && (
                          <div className="modern-request-description">
                            <p className="mb-0">{request.description}</p>
                          </div>
                        )}

                        {request.rejectionReason && (
                          <div className="modern-request-rejection">
                            <i className="bx bx-info-circle text-warning"></i>
                            <span><strong>Red Sebebi:</strong> {request.rejectionReason}</span>
                          </div>
                        )}
                      </div>

                      <div className="modern-request-card-footer">
                        <div className="modern-request-footer-info">
                          <small className="text-muted">
                            Talep Eden: {request.requestedBy}
                          </small>
                        </div>
                        <div className="modern-request-footer-actions">
                          <button className="modern-hr-btn modern-hr-btn-sm modern-hr-btn-outline">
                            <i className="bx bx-show"></i>
                            Detay
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default IdariIslerTaleplerimPage;
